import { useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  PlusOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDate, formatPhone } from '@/lib/format';
import { whatsappUrl } from '@/lib/whatsapp';
import { PublishListingModal } from './PublishListingModal';
import {
  categoryLabel,
  useCategoriesQuery,
  useListingsQuery,
  type Listing,
  type ListingFeed,
} from './hooks';

const { Title, Text, Paragraph } = Typography;

const FEEDS: ListingFeed[] = ['demand', 'offer', 'notice'];
const DEFAULT_FEED: ListingFeed = 'demand';

function isFeed(value: string | null): value is ListingFeed {
  return value !== null && (FEEDS as string[]).includes(value);
}

// Раздел «Объявления» (V1.5) — ленты Starling Vision внутри кабинета оптики:
// три вкладки (VD4: feed обязателен), фильтр по категории, поиск, докрутка
// «Показать ещё» (keyset-курсор VD3) и публикация от имени своей организации.
// Информационная структура карточек — как в Vision-ленте (frontend/vision/src/
// feed/ListingCard.tsx): заголовок + категория, город · организация, дата.
// Детали — Drawer из уже загруженного объекта, без отдельного запроса.
export function ListingsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
  const [selected, setSelected] = useState<Listing | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);

  const feedParam = searchParams.get('feed');
  const feed: ListingFeed = isFeed(feedParam) ? feedParam : DEFAULT_FEED;
  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? '';

  const categories = useCategoriesQuery().data?.items;
  const listingsQuery = useListingsQuery({
    feed,
    ...(q ? { q } : {}),
    ...(category ? { category } : {}),
  });

  const items = listingsQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const isFiltered = Boolean(q || category);

  const updateParams = (mutate: (next: URLSearchParams) => void): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div>
      <Space
        style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}
        wrap
      >
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('listings.title')}
          </Title>
          <Text type="secondary">{t('listings.subtitle')}</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setPublishOpen(true)}>
          {t('listings.publish.button')}
        </Button>
      </Space>

      <Tabs
        activeKey={feed}
        onChange={(key) => updateParams((next) => next.set('feed', key))}
        items={FEEDS.map((f) => ({ key: f, label: t(`listings.tabs.${f}`) }))}
        style={{ marginTop: 8 }}
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(value) =>
            updateParams((next) => {
              if (value) next.set('q', value);
              else next.delete('q');
            })
          }
          placeholder={t('listings.search_placeholder')}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          value={category || undefined}
          onChange={(value?: string) =>
            updateParams((next) => {
              if (value) next.set('category', value);
              else next.delete('category');
            })
          }
          placeholder={t('listings.category_all')}
          style={{ minWidth: 220 }}
          options={(categories ?? []).map((c) => ({
            value: c.id,
            label: categoryLabel(categories, c.id, i18n.language),
          }))}
        />
      </Space>

      {listingsQuery.error != null && <ErrorAlert error={listingsQuery.error} />}

      {listingsQuery.isLoading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : items.length === 0 && listingsQuery.error == null ? (
        <Empty description={isFiltered ? t('listings.empty_filtered') : t('listings.empty')} />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {items.map((listing) => (
            <Card
              key={listing.id}
              hoverable
              size="small"
              onClick={() => setSelected(listing)}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Space
                style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Text strong>{listing.title}</Text>
                <Tag>{categoryLabel(categories, listing.category_id, i18n.language)}</Tag>
              </Space>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <EnvironmentOutlined style={{ marginRight: 4 }} />
                  {listing.city}
                  {listing.district ? ` · ${listing.district}` : ''} · {listing.organization_name}
                </Text>
              </div>
              <div style={{ marginTop: 2 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDate(listing.published_at)}
                </Text>
              </div>
            </Card>
          ))}
          {listingsQuery.hasNextPage && (
            <Button
              onClick={() => void listingsQuery.fetchNextPage()}
              loading={listingsQuery.isFetchingNextPage}
              block
            >
              {t('listings.load_more')}
            </Button>
          )}
        </Space>
      )}

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.title}
        width={480}
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space wrap>
              <Tag>{categoryLabel(categories, selected.category_id, i18n.language)}</Tag>
              <Text type="secondary">
                {t('listings.detail.published')}: {formatDate(selected.published_at)}
              </Text>
            </Space>
            <div>
              <Text type="secondary">{t('listings.detail.organization')}</Text>
              <div>
                <Text strong>{selected.organization_name}</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                <EnvironmentOutlined style={{ marginRight: 4 }} />
                {selected.city}
                {selected.district ? ` · ${selected.district}` : ''}
              </Text>
            </div>
            <div>
              <Text type="secondary">{t('listings.detail.description')}</Text>
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                {selected.description}
              </Paragraph>
            </div>
            <div>
              <Text type="secondary">{t('listings.detail.contact')}</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', marginTop: 4 }}>
                {selected.contact_phone &&
                  (selected.contact_whatsapp ? (
                    // №22: телефон с WhatsApp — сам номер ведёт в чат wa.me
                    // с приветствием по объявлению (как в Vision).
                    <a
                      href={whatsappUrl(
                        selected.contact_phone,
                        t('listings.detail.whatsapp_text', { title: selected.title }),
                      )}
                      target="_blank"
                      rel="noreferrer"
                      title={t('listings.detail.whatsapp')}
                    >
                      <WhatsAppOutlined style={{ marginRight: 6, color: '#25d366' }} />
                      {formatPhone(selected.contact_phone)}
                    </a>
                  ) : (
                    <span>
                      <PhoneOutlined style={{ marginRight: 6 }} />
                      {formatPhone(selected.contact_phone)}
                    </span>
                  ))}
                {selected.contact_telegram && (
                  <a
                    href={`https://t.me/${selected.contact_telegram}`}
                    target="_blank"
                    rel="noreferrer"
                    title={t('listings.detail.telegram')}
                  >
                    <SendOutlined style={{ marginRight: 6, color: '#229ed9' }} />@
                    {selected.contact_telegram}
                  </a>
                )}
              </div>
            </div>
            {selected.geo_ref && (
              <a href={selected.geo_ref} target="_blank" rel="noreferrer">
                {t('listings.detail.geo')}
              </a>
            )}
          </Space>
        )}
      </Drawer>

      <PublishListingModal
        open={publishOpen}
        defaultFeed={feed}
        onClose={() => setPublishOpen(false)}
      />
    </div>
  );
}
